import classnames from 'classnames'
import { FC, useMemo, useState } from 'react'
import { Collapse } from 'react-collapse'

import { PackageNode, ArtworkNode } from 'gql'
import { formatCurrency, getImageUrl, time } from 'utils'
import { useStore as useFilterStore } from 'components/contexts/event-page-filters'
import { Markdown } from '~shared'
import { Button, Typography, Alert, Flex } from '~ui'

import styles from './Package.module.scss'

type PackageProps = Pick<
  PackageNode,
  'availableQuantity' | 'shortDescription' | 'longDescription' | 'maxSplit' | 'price' | 'name'
> & { image: Pick<PackageNode['image'], 'id' | 'path' | 'extension'> } & Pick<
    ArtworkNode,
    'eventExtendedStatus' | 'salesOnDateLocal'
  > & {
    defaultOpen?: boolean
    onClick: () => void
    onOpen?: () => void
  }

const Package: FC<PackageProps> = ({
  availableQuantity,
  shortDescription,
  longDescription,
  maxSplit,
  price,
  image,
  name,
  eventExtendedStatus,
  salesOnDateLocal,
  defaultOpen = false,
  onClick,
  onOpen,
}) => {
  const { formQuantity } = useFilterStore()

  const _availableQuantity = useMemo(
    () => Math.min(maxSplit, availableQuantity),
    [maxSplit, availableQuantity]
  )

  const amountNotAvailable = _availableQuantity < formQuantity

  const soldOut = _availableQuantity < 1

  const saleNotStarted = eventExtendedStatus === 'sales_not_started'

  const disabledCheckout = amountNotAvailable || soldOut || saleNotStarted

  const [isOpened, setIsOpened] = useState(defaultOpen)

  return (
    <div className={styles.package}>
      <div className={styles.header}>
        <Flex justifyContent="space-between" alignItems="center">
          <Typography.Text bold size="lg">
            {name}
          </Typography.Text>

          <Button
            icon="arrow"
            iconSize="1.5em"
            variant="secondary"
            className={classnames(styles.toggle, { [styles['toggle-opened']]: isOpened })}
            onClick={() => {
              setIsOpened((prev) => !prev)
              if (!isOpened) onOpen?.()
            }}
          />
        </Flex>

        <div className={styles.content}>
          <img
            src={getImageUrl(image, 128, 128)}
            className={styles.image}
            height={50}
            width={50}
            alt={name}
          />
          <Markdown variant="standart" md={shortDescription} />
        </div>
      </div>
      <Collapse isOpened={isOpened}>
        <div className={styles.body}>
          <Typography.Text bold>{formatCurrency(price, 0)}</Typography.Text>
          <Markdown variant="standart" md={longDescription} />
          {saleNotStarted && (
            <Alert mode="warning">
              {`Sale will start at ${time.formatDate(salesOnDateLocal, 'long')}`}
            </Alert>
          )}
          <button
            disabled={disabledCheckout}
            className="btn btn-primary"
            onClick={onClick}
            type="button"
          >
            {soldOut ? 'Sold Out' : 'Add to Checkout'}
          </button>
        </div>
      </Collapse>
    </div>
  )
}

export default Package
